import React, { useEffect, useState, useRef, useCallback } from 'react';
import api from '../utils/api';
import { useCart } from '../context/CartContext';
import { useMetalRates } from '../context/MetalRatesContext';
import { toast } from 'sonner';
import { subscribeToStockChanges } from '../utils/stockSync';
import { HomeSEO } from '../components/SEOHead';

// Extracted section components
import { HOMEPAGE_CONFIG } from '../components/home/homeConfig';
import { HeroBannerCarousel } from '../components/home/HeroBannerCarousel';
import { ShopByCategory } from '../components/home/ShopByCategory';
import { CollectionsSection } from '../components/home/CollectionsSection';
import { ExploreJewellery } from '../components/home/ExploreJewellery';
import { ShopByGender } from '../components/home/ShopByGender';
import { QualityBadges } from '../components/home/QualityBadges';

export default function HomePage() {
  const [products, setProducts] = useState([]);
  const [sections, setSections] = useState(null);
  const [loading, setLoading] = useState(true);
  const { metalRates } = useMetalRates();
  const { addToCart } = useCart();
  const fetchDataRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [productsRes, sectionsRes] = await Promise.all([
        api.get('/products', { params: { limit: 100 } }),
        api.get('/homepage/sections')
      ]);
      const productsData = productsRes.data.products || productsRes.data;
      setProducts(Array.isArray(productsData) ? productsData : []);
      setSections(sectionsRes.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchDataRef.current = fetchData; }, [fetchData]);

  useEffect(() => {
    const unsubscribe = subscribeToStockChanges(() => {
      if (fetchDataRef.current) fetchDataRef.current();
    });
    return () => unsubscribe();
  }, []);

  // Use API data if loaded, fallback only if API failed (sections loaded but empty)
  const heroBanners = !loading && sections?.hero_banners?.length > 0 ? sections.hero_banners : (!loading ? HOMEPAGE_CONFIG.heroBanners : null);
  const categories = !loading && sections?.categories?.length > 0 ? sections.categories : (!loading ? HOMEPAGE_CONFIG.categories : null);
  const collections = !loading && sections?.collections?.length > 0 ? sections.collections : (!loading ? HOMEPAGE_CONFIG.collections : null);
  const exploreItems = !loading && sections?.explore_jewellery?.length > 0 ? sections.explore_jewellery : (!loading ? HOMEPAGE_CONFIG.exploreJewellery : null);
  const genderCategories = !loading && sections?.gender_categories?.length > 0 ? sections.gender_categories : (!loading ? HOMEPAGE_CONFIG.genderCategories : null);

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      <HomeSEO />
      {loading ? (
        <div className="h-[50vh] md:h-[60vh] lg:h-[70vh] bg-[#1a3a5c] flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#C9A967] border-t-transparent mx-auto mb-3" />
            <p className="text-white/60 text-sm font-serif tracking-wider">MARISSA</p>
          </div>
        </div>
      ) : (
        <>
          {heroBanners && <HeroBannerCarousel banners={heroBanners} />}
          {categories && <ShopByCategory categories={categories} products={products} />}
          {collections && <CollectionsSection collections={collections} />}
          {exploreItems && <ExploreJewellery exploreItems={exploreItems} products={products} />}
          {genderCategories && <ShopByGender genderCategories={genderCategories} />}
        </>
      )}
      <QualityBadges />
    </div>
  );
}
